/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Handles valid step from URL.
 * 
 * @param {string} stepId - Step ID to navigate to
 * @param {Function | undefined} onStepChange - Step change handler
 * @returns {void}
 */
export function handleValidStepFromUrl(
  stepId: string,
  onStepChange?: (stepId: string) => void
): void {
  if (onStepChange) {
    onStepChange(stepId);
  }
}

/**
 * Handles invalid step from URL by redirecting to first step.
 * 
 * @param {string[]} stepIdOrder - Ordered list of step IDs
 * @param {string} stepParamName - URL parameter name for step
 * @param {Function} setSearchParams - Function to set search params
 * @param {Function | undefined} onStepChange - Step change handler
 * @returns {void}
 */
export function handleInvalidStepFromUrl(
  stepIdOrder: string[],
  stepParamName: string,
  setSearchParams: (updater: (prev: URLSearchParams) => URLSearchParams, options?: { replace?: boolean }) => void,
  onStepChange?: (stepId: string) => void
): void {
  const firstStep = stepIdOrder[0];
  setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set(stepParamName, firstStep);
    return next;
  }, { replace: true });
  if (onStepChange) {
    onStepChange(firstStep);
  }
}

/**
 * Handles missing step in URL by adding current step.
 * 
 * @param {string | undefined} currentStepId - Current step ID
 * @param {string} stepParamName - URL parameter name for step
 * @param {Function} setSearchParams - Function to set search params
 * @returns {void}
 */
function handleMissingStepInUrl(
  currentStepId: string | undefined,
  stepParamName: string,
  setSearchParams: (updater: (prev: URLSearchParams) => URLSearchParams, options?: { replace?: boolean }) => void
): void {
  if (currentStepId) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(stepParamName, currentStepId);
      return next;
    }, { replace: true });
  }
}

/**
 * Checks if step is valid.
 * 
 * @param {string} stepId - Step ID to check
 * @param {string[]} stepIdOrder - Ordered list of step IDs
 * @returns {boolean} True if valid
 */
function isValidStepId(stepId: string, stepIdOrder: string[]): boolean {
  return stepIdOrder.includes(stepId);
}

/**
 * Processes step from URL.
 * 
 * @param {string | null} urlStep - Step from URL
 * @param {string | undefined} currentStepId - Current step ID
 * @param {string[]} stepIdOrder - Ordered list of step IDs
 * @param {string} stepParamName - URL parameter name for step
 * @param {Function} setSearchParams - Function to set search params
 * @param {Function | undefined} onStepChange - Step change handler
 * @returns {void}
 */
export function processStepFromUrl(
  urlStep: string | null,
  currentStepId: string | undefined,
  stepIdOrder: string[],
  stepParamName: string,
  setSearchParams: (updater: (prev: URLSearchParams) => URLSearchParams, options?: { replace?: boolean }) => void,
  onStepChange?: (stepId: string) => void
): void {
  if (!urlStep) {
    handleMissingStepInUrl(currentStepId, stepParamName, setSearchParams);
    return;
  }

  if (urlStep === currentStepId) {
    return;
  }

  if (isValidStepId(urlStep, stepIdOrder)) {
    handleValidStepFromUrl(urlStep, onStepChange);
  } else {
    handleInvalidStepFromUrl(stepIdOrder, stepParamName, setSearchParams, onStepChange);
  }
}
